import { Link } from "react-router-dom";
import { MapPin, Plane, ArrowRight, Star, Sparkles } from "lucide-react";

const DESTINATIONS = [
  {
    name: "New York",
    sub: "Manhattan · 5-Star",
    desc: "Rooftop suites overlooking the Manhattan skyline. Walking distance to Wall Street, Madison Avenue, and Times Square.",
    img: "https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=1200&q=80",
  },
  {
    name: "Myrtle Beach",
    sub: "Atlantic Coast · Oceanfront",
    desc: "Pristine oceanfront resorts with private beaches, championship golf access, and four-star amenities.",
    img: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&q=80",
  },
  {
    name: "Orlando",
    sub: "Themed Luxury · Family",
    desc: "Opulent resorts adjacent to world-class theme parks. Ideal for high-value client retreats and family incentive trips.",
    img: "https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=1200&q=80",
  },
  {
    name: "Las Vegas",
    sub: "The Strip · Penthouse Floors",
    desc: "Penthouse-level suites on the Strip with private VIP lounges, concierge gaming hosts, and world-class entertainment.",
    img: "https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?w=1200&q=80",
  },
  {
    name: "Cancun",
    sub: "Caribbean · All-Inclusive",
    desc: "Beachfront all-inclusive resorts on turquoise Caribbean waters. Cabanas, spas, and gourmet dining included.",
    img: "https://images.unsplash.com/photo-1510097467424-192d713fd8b2?w=1200&q=80",
  },
  {
    name: "Rome",
    sub: "Historic Italy · Boutique",
    desc: "Historic boutique hotels in the heart of the Eternal City. Steps from the Colosseum, the Vatican, and Trastevere.",
    img: "https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=1200&q=80",
  },
  {
    name: "Brazil",
    sub: "Rio de Janeiro · Copacabana",
    desc: "Iconic beachfront luxury overlooking Copacabana and Ipanema. Sugarloaf views, samba culture, and tropical resort living.",
    img: "https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=1200&q=80",
  },
];

export default function DestinationsPage() {
  return (
    <div data-testid="destinations-page" className="bg-[#020617]">
      {/* HERO + VIDEO */}
      <section className="relative overflow-hidden aios-starfield">
        <div className="absolute inset-0 aios-grid-overlay opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/30 via-transparent to-[#020617]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <span className="text-red-500 font-display text-xs tracking-[0.3em] font-bold">
                THE 130+ LUXURY DESTINATIONS
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white mt-4 leading-[1.05]">
                Seven cities. <br />
                <span className="text-red-500">130+ properties.</span> One portal.
              </h1>
              <p className="mt-6 text-lg text-slate-300 leading-relaxed">
                A preview of the corporate travel inventory unlocked by your $485 portal activation &mdash; pre-negotiated 4 &amp; 5-star resorts, business hotels, and beachfront escapes across the destinations your team and clients actually want to be in.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link to="/activate" data-testid="destinations-cta-activate" className="aios-btn-primary text-sm aios-pulse">
                  Activate Portal &middot; $485
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/test-drive" data-testid="destinations-cta-testdrive" className="aios-btn-secondary text-sm">
                  $9.95 Test Drive First
                </Link>
              </div>
            </div>

            {/* Destinations Tour Video */}
            <div className="lg:col-span-6">
              <div
                data-testid="destinations-tour-video-container"
                className="relative aspect-video bg-[#0B1221] border border-slate-700 overflow-hidden"
              >
                <video
                  data-testid="destinations-tour-video"
                  src="/assets/dest-destinations-tour.mp4"
                  poster="https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80"
                  autoPlay
                  loop
                  muted
                  playsInline
                  controls
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to existing dest-resort if tour video not yet generated
                    if (!e.currentTarget.dataset.fallback) {
                      e.currentTarget.dataset.fallback = "1";
                      e.currentTarget.src = "/assets/dest-resort.mp4";
                    }
                  }}
                />
                <div className="absolute top-4 left-4 bg-red-600 px-3 py-1 font-display text-[10px] tracking-[0.25em] text-white font-bold pointer-events-none">
                  ● DESTINATIONS REEL
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-slate-500 font-display tracking-widest uppercase">
                <span>New York</span><span>·</span>
                <span>Myrtle Beach</span><span>·</span>
                <span>Orlando</span><span>·</span>
                <span>Las Vegas</span><span>·</span>
                <span>Cancun</span><span>·</span>
                <span>Rome</span><span>·</span>
                <span>Brazil</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DESTINATION GRID */}
      <section className="py-20 lg:py-28 bg-[#0B1221]" data-testid="destinations-grid-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <span className="text-red-500 font-display text-xs tracking-[0.3em] font-bold">
              FEATURED DESTINATIONS
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4 tracking-tighter">
              The cities your team &amp; <br />
              <span className="text-red-500">closing clients</span> actually fly to.
            </h2>
            <p className="mt-6 text-slate-400 text-lg leading-relaxed">
              Each property is independently vetted, pre-rate-negotiated, and bookable directly through your A.I.O sourcing portal &mdash; with corporate-tier pricing your solo travel team simply can&apos;t access.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {DESTINATIONS.map((d, idx) => (
              <div
                key={d.name}
                data-testid={`destination-card-${idx}`}
                className="aios-card overflow-hidden group"
              >
                <div className="aios-card-accent" />
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={d.img}
                    alt={`${d.name} luxury destination`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/30 to-transparent" />
                  <div className="absolute top-3 left-3 bg-red-600 px-3 py-1 font-display text-[10px] tracking-[0.25em] text-white font-bold">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="font-display font-extrabold text-white text-2xl tracking-tight">
                      {d.name}
                    </div>
                    <div className="flex items-center gap-1 text-slate-300 text-xs mt-1 font-display tracking-widest uppercase">
                      <MapPin className="w-3 h-3 text-red-500" /> {d.sub}
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-1 text-yellow-400 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed">{d.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-14 text-center">
            <p className="text-slate-400 mb-6">
              These seven cities are a small sample. Your portal unlocks <span className="text-white font-semibold">130+ full destinations worldwide</span>.
            </p>
            <Link to="/activate" data-testid="destinations-bottom-cta" className="aios-btn-primary text-sm inline-flex">
              <Plane className="w-4 h-4" />
              Activate Full Portal &middot; $485
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SHALLOW PROOF BAR */}
      <section className="py-16 bg-[#020617] border-y border-slate-900">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <Sparkles className="w-7 h-7 text-red-500 mx-auto" />
          <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-white mt-4 tracking-tighter">
            One $485 portal license. <span className="text-red-500">Unlimited 4-to-5-day vouchers.</span>
          </h3>
          <p className="text-slate-400 mt-3 max-w-2xl mx-auto">
            Hand them to tenants, clients, or your top team members &mdash; we cover the room rate, the traveler pays only local nightly taxes &amp; resort fees.
          </p>
        </div>
      </section>
    </div>
  );
}
